import { forwardRef, type HTMLAttributes, type InputHTMLAttributes, type MouseEvent, type ReactNode } from 'react';

const OWN_CLICK = 'input, textarea, select, button, a[href], [role="listbox"], [role="option"], [role="menu"], [role^="menuitem"]';

/**
 * Building blocks of a "sheet" (styles.css `.sheet-table`): a table edited in place like a spreadsheet, grouped in
 * sections, as the quote (F22) and next the packlist editor (owner remark 20). The table itself is
 * `<table className="table sheet-table">`; its first column holds the section's name on the section's first row.
 */

/** One section: a `<tbody>` named for screen readers. */
/** `className`, `onDragOver`, `onDrop`: the quote's sections move whole (owner 2026-10-23 20:46). */
/** `sectionId`: as `data-sheet-section` on the `<tbody>`, so the section's rows can be found by its id. */
export function SheetSection({ name, sectionId, children, className, onDragOver, onDrop }: { name: string; sectionId?: string; children: ReactNode } & Pick<HTMLAttributes<HTMLTableSectionElement>, 'className' | 'onDragOver' | 'onDrop'>) {
  return (
    <tbody className={className ? `sheet-section ${className}` : 'sheet-section'} aria-label={`Sekcja ${name}`} data-sheet-section={sectionId} onDragOver={onDragOver} onDrop={onDrop}>
      {children}
    </tbody>
  );
}

/** A row under a section's rows (e.g. its quick picker): an empty section cell, then one cell over the rest. */
/** `after`: empty columns after the field's cell, so the field stays within its own column(s) (the quote: "Marka"). */
/**
 * `focusOnClick`: the whole row is a click target that focuses its field (the quote, owner 2026-10-23 19:29: the field
 * shows only on hover of its row). A click on a control of its own (the field, a button, a menu) is left to it.
 */
/** `head`: what stands in the section cell (an empty section's name, so the add row is its only row). */
export function SheetAddRow({ span, after = 0, focusOnClick = false, head, children }: { span: number; after?: number; focusOnClick?: boolean; head?: ReactNode; children: ReactNode }) {
  const onClick = (e: MouseEvent<HTMLTableRowElement>) => {
    if (e.target instanceof Element && e.target.closest(OWN_CLICK)) return;
    e.currentTarget.querySelector<HTMLInputElement>('input:not([disabled])')?.focus();
  };
  return (
    <tr className={focusOnClick ? 'sheet-add sheet-add-click' : 'sheet-add'} onClick={focusOnClick ? onClick : undefined}>
      <th scope="row" className="sheet-section-cell">
        {head}
      </th>
      <td colSpan={span}>{children}</td>
      {after > 0 && <td colSpan={after} />}
    </tr>
  );
}

/** A cell's input: looks like text until pointed at or focused; `numeric` aligns right with tabular digits. */
export const CellInput = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & { numeric?: boolean }>(function CellInput({ numeric, className, ...rest }, ref) {
  return <input ref={ref} className={['cell-input', numeric ? 'num' : '', className ?? ''].filter(Boolean).join(' ')} inputMode={numeric ? 'decimal' : undefined} autoComplete="off" spellCheck={false} {...rest} />;
});
